import { EXPENSE_CATEGORIES, financialPeriodKey, round2, totalFinancialMonths, type ExpenseCategory, type FinancialGranularity, type FinancialMonth } from "@torre/expenses";

// Renglones de los estados financieros, listos para pintar en tabla o bajar en
// CSV: una fila por concepto, una columna por periodo y el total del rango.

export type StatementRowKind = "header" | "line" | "subtotal" | "total" | "memo";

export interface StatementRow {
  key: string;
  kind: StatementRowKind;
  /** Texto ya resuelto (una categoría); si no, la interfaz traduce `key`. */
  label?: string;
  /** Un valor por periodo; vacío en los encabezados. */
  values: (number | null)[];
  total: number | null;
  /** Se muestra como porcentaje (márgenes). */
  percent?: boolean;
}

const header = (key: string): StatementRow => ({ key, kind: "header", values: [], total: null });

/** Primer mes del trimestre o del año al que pertenece `period` (para no pedir periodos a medias). */
export function alignPeriodStart(period: string, granularity: FinancialGranularity): string {
  if (granularity === "month") return period;
  const [y, mo] = period.split("-").map(Number);
  const first = granularity === "year" ? 1 : Math.floor((mo - 1) / 3) * 3 + 1;
  return `${y}-${String(first).padStart(2, "0")}`;
}

/** Cambia el signo sin dejar un «-0» (que se pintaría como -$0.00). */
const neg = (value: number) => (value === 0 ? 0 : -value);

const ratio = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 1000) / 1000 : null);

/**
 * Estado de resultados. `lossByPeriod` (opcional) agrega el renglón informativo
 * «de los cuales, pérdidas»: no se resta, ya está dentro de los gastos.
 */
export function incomeStatementRows(periods: readonly FinancialMonth[], categoryLabel: (c: ExpenseCategory) => string, lossByPeriod?: Record<string, number> | null): StatementRow[] {
  const total = totalFinancialMonths(periods);
  const line = (key: string, kind: StatementRowKind, pick: (m: FinancialMonth) => number, label?: string): StatementRow => ({ key, kind, label, values: periods.map(pick), total: pick(total) });
  // Las del sistema en su orden y, después, las categorías propias de la compañía con gasto (#1255).
  const present = new Set<string>();
  for (const m of periods) for (const [c, v] of Object.entries(m.opex)) if ((v || 0) !== 0) present.add(c);
  const system = EXPENSE_CATEGORIES as readonly string[];
  const categories: ExpenseCategory[] = [...system.filter((c) => present.has(c)), ...[...present].filter((c) => !system.includes(c))];
  const rows: StatementRow[] = [
    header("income"),
    line("revenue", "line", (m) => m.revenue),
    line("otherIncome", "line", (m) => m.otherIncome),
    line("totalIncome", "subtotal", (m) => m.totalIncome),
    // Costo del servicio = nómina + infraestructura (nube, hosting y APIs que consume el producto).
    line("costOfServicePayroll", "line", (m) => neg(m.costOfServicePayroll)),
    line("costOfServiceInfrastructure", "line", (m) => neg(m.costOfServiceInfrastructure)),
    line("costOfService", "subtotal", (m) => neg(m.costOfService)),
    line("grossProfit", "subtotal", (m) => m.grossProfit),
    header("opex"),
    ...categories.map((c) => line(`opex.${c}`, "line", (m) => neg(m.opex[c] || 0), categoryLabel(c))),
  ];
  if (periods.some((m) => m.refunds !== 0)) rows.push(line("refunds", "line", (m) => m.refunds));
  rows.push(line("opexTotal", "subtotal", (m) => neg(m.opexTotal)));
  rows.push(line("depreciation", "line", (m) => neg(m.depreciation)));
  rows.push(line("operatingProfit", "subtotal", (m) => m.operatingProfit));
  rows.push(line("financialIncome", "line", (m) => m.financialIncome));
  rows.push(line("financialCost", "line", (m) => neg(m.financialCost)));
  rows.push(line("profitBeforeTaxes", "subtotal", (m) => m.profitBeforeTaxes));
  rows.push(line("taxes", "line", (m) => neg(m.taxes)));
  rows.push(line("netIncome", "total", (m) => m.netIncome));
  rows.push({ key: "netMargin", kind: "memo", percent: true, values: periods.map((m) => ratio(m.netIncome, m.totalIncome)), total: ratio(total.netIncome, total.totalIncome) });
  if (lossByPeriod) {
    const values = periods.map((m) => round2(lossByPeriod[m.period] || 0));
    rows.push({ key: "lossesMemo", kind: "memo", values, total: round2(values.reduce((s, v) => s + v, 0)) });
  }
  return rows;
}

/** Estado de flujo de efectivo: operación, inversión y financiamiento, con el efectivo al inicio y al final. */
export function cashFlowRows(periods: readonly FinancialMonth[], openingCash: number): StatementRow[] {
  const total = totalFinancialMonths(periods);
  const line = (key: string, kind: StatementRowKind, pick: (m: FinancialMonth) => number): StatementRow => ({ key, kind, values: periods.map(pick), total: pick(total) });
  let running = openingCash;
  const opening: number[] = [];
  const closing: number[] = [];
  for (const m of periods) {
    opening.push(round2(running));
    running = round2(running + m.cashNet);
    closing.push(running);
  }
  return [
    header("operating"),
    line("cashCollections", "line", (m) => m.cashCollections),
    line("cashPayroll", "line", (m) => neg(m.cashPayroll)),
    line("cashOperatingPaid", "line", (m) => neg(m.cashOperatingPaid)),
    line("cashTaxes", "line", (m) => neg(m.cashTaxes)),
    line("cashOperating", "subtotal", (m) => m.cashOperating),
    header("investing"),
    line("cashEquipment", "line", (m) => neg(m.cashEquipment)),
    line("cashInterest", "line", (m) => m.cashInterest),
    line("cashInvesting", "subtotal", (m) => m.cashInvesting),
    header("financing"),
    line("cashPartnerFunding", "line", (m) => m.cashPartnerFunding),
    line("cashFinancing", "subtotal", (m) => m.cashFinancing),
    line("cashNet", "total", (m) => m.cashNet),
    { key: "cashOpening", kind: "memo", values: opening, total: round2(openingCash) },
    { key: "cashClosing", kind: "memo", values: closing, total: round2(running) },
  ];
}

/** Pérdidas de cada mes sumadas al periodo (mes, trimestre o año) que le toca. */
export function lossesByPeriod(months: readonly { period: string; total: number }[], granularity: FinancialGranularity): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of months) {
    const key = financialPeriodKey(m.period, granularity);
    out[key] = round2((out[key] || 0) + m.total);
  }
  return out;
}

const csvCell = (value: string | number | null) => {
  const text = value == null ? "" : String(value);
  return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** CSV de una tabla de estado financiero (montos sin formato, para la hoja de cálculo). */
export function statementCsv(rows: readonly StatementRow[], periodLabels: readonly string[], labelOf: (row: StatementRow) => string, headers: { concept: string; total: string }): string {
  const lines = [[headers.concept, ...periodLabels, headers.total].map(csvCell).join(",")];
  for (const row of rows) {
    if (row.kind === "header") lines.push(csvCell(labelOf(row)));
    else lines.push([labelOf(row), ...row.values, row.total].map(csvCell).join(","));
  }
  return lines.join("\n");
}
